/**
 * DataQCDetailTable Component
 * 
 * A professional data table with adaptive theming, sticky headers, and visual diffing
 * for AI-modified rows. Optimized for displaying Polars DataFrames and QC results.
 * 
 * Features:
 * - Sticky header row
 * - Visual diffing for AI-modified rows (10% opacity success background)
 * - JetBrains Mono for all data cells
 * - Inter for headers
 * - Responsive design with horizontal scroll
 * - Theme-aware borders and backgrounds
 * 
 * @example
 * ```tsx
 * <DataQCDetailTable
 *   data={qcResults}
 *   modifiedRowIds={[2, 5, 8]}
 *   highlightColumn="status"
 * />
 * ```
 */
import React, { useMemo } from 'react';
import { cn } from '@/utils/cn';

type CellValue = string | number | boolean | null;

interface Column {
  /** Unique column identifier */
  key: string;
  /** Display label for header */
  label: string;
  /** Column width (default: auto) */
  width?: string;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Custom cell renderer */
  render?: (value: CellValue, row: Record<string, CellValue>, rowIndex: number) => React.ReactNode;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Column type for semantic formatting */
  type?: 'text' | 'number' | 'date' | 'status' | 'code';
}

interface DataQCDetailTableProps {
  /** Array of data rows */
  data: Record<string, string | number | boolean | null>[];
  /** Column definitions */
  columns: Column[];
  /** Row IDs that were modified by AI (for visual diffing) */
  modifiedRowIds?: number[];
  /** Optional column to highlight */
  highlightColumn?: string;
  /** Show row numbers */
  showRowNumbers?: boolean;
  /** Enable sticky header */
  stickyHeader?: boolean;
  /** Maximum table height (enables vertical scroll) */
  maxHeight?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * DataQCDetailTable - Professional data table with AI modification tracking
 */
export const DataQCDetailTable: React.FC<DataQCDetailTableProps> = ({
  data,
  columns,
  modifiedRowIds = [],
  highlightColumn,
  showRowNumbers = true,
  stickyHeader = true,
  maxHeight = '600px',
  isLoading = false,
  emptyMessage = 'No data available',
  className,
}) => {
  // Create modified rows set for O(1) lookup
  const modifiedRowsSet = useMemo(
    () => new Set(modifiedRowIds),
    [modifiedRowIds]
  );

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="w-full overflow-hidden rounded-lg border border-border bg-bg-surface">
        <div className="p-8 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-interactive-primary border-r-transparent" />
          <p className="mt-4 text-sm text-text-secondary font-ui">Loading data...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="w-full overflow-hidden rounded-lg border border-border bg-bg-surface">
        <div className="p-12 text-center">
          <p className="text-text-secondary font-ui">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-lg',
        'border border-border bg-bg-surface shadow-sm',
        className
      )}
    >
      <div
        className="overflow-auto"
        style={{ maxHeight }}
      >
        <table className="w-full border-collapse">
          {/* Sticky Header */}
          <thead
            className={cn(
              'bg-bg-elevated border-b-2 border-border-strong',
              stickyHeader && 'sticky top-0 z-10'
            )}
          >
            <tr>
              {/* Row Number Column */}
              {showRowNumbers && (
                <th
                  className={cn(
                    'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                    'text-text-secondary font-ui border-r border-border-subtle',
                    'bg-bg-elevated'
                  )}
                  style={{ width: '60px' }}
                >
                  #
                </th>
              )}

              {/* Data Columns */}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    'px-4 py-3 text-xs font-semibold uppercase tracking-wider',
                    'text-text-secondary font-ui border-r border-border-subtle last:border-r-0',
                    'bg-bg-elevated',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    highlightColumn === column.key && 'bg-qc-info-bg'
                  )}
                  style={{ width: column.width }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="divide-y divide-border-subtle">
            {data.map((row, rowIndex) => {
              const isModified = modifiedRowsSet.has(rowIndex);

              return (
                <tr
                  key={rowIndex}
                  className={cn(
                    'transition-colors hover:bg-bg-hover',
                    // Visual diffing: AI-modified rows get subtle success background
                    isModified && 'qc-success-bg border-l-2 border-l-qc-success'
                  )}
                  title={isModified ? 'Modified by AI' : undefined}
                >
                  {/* Row Number */}
                  {showRowNumbers && (
                    <td
                      className={cn(
                        'px-4 py-3 text-sm font-medium font-data',
                        'text-text-tertiary border-r border-border-subtle',
                        'whitespace-nowrap'
                      )}
                    >
                      {rowIndex + 1}
                    </td>
                  )}

                  {/* Data Cells */}
                  {columns.map((column) => {
                    const value = row[column.key];
                    const cellContent = column.render
                      ? column.render(value, row, rowIndex)
                      : formatCellValue(value, column.type);

                    return (
                      <td
                        key={column.key}
                        className={cn(
                          'px-4 py-3 text-sm font-data',
                          'text-text-primary border-r border-border-subtle last:border-r-0',
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right',
                          column.type === 'code' && 'font-mono text-xs',
                          highlightColumn === column.key && 'bg-qc-info-bg font-semibold'
                        )}
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer Info */}
      <div className="border-t border-border bg-bg-subtle px-4 py-2 flex items-center justify-between">
        <div className="text-xs text-text-secondary font-ui">
          {data.length} {data.length === 1 ? 'row' : 'rows'}
          {modifiedRowIds.length > 0 && (
            <span className="ml-2 text-qc-success">
              • {modifiedRowIds.length} modified by AI
            </span>
          )}
        </div>
        <div className="text-xs text-text-tertiary font-ui">
          Data font: JetBrains Mono
        </div>
      </div>
    </div>
  );
};

/**
 * Format cell value based on type
 */
function formatCellValue(value: CellValue, type?: Column['type']): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-text-tertiary italic">null</span>;
  }

  switch (type) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value;
    
    case 'date':
      if (typeof value === 'boolean') {
        return String(value);
      }
      try {
        return new Date(value).toLocaleDateString();
      } catch {
        return value;
      }
    
    case 'status':
      return (
        <span
          className={cn(
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-ui',
            getStatusStyles(String(value))
          )}
        >
          {String(value)}
        </span>
      );
    
    case 'code':
      return (
        <code className="px-1.5 py-0.5 rounded bg-bg-subtle text-xs font-mono">
          {String(value)}
        </code>
      );
    
    default:
      return String(value);
  }
}

/**
 * Get status badge styles
 */
function getStatusStyles(status: string): string {
  const statusLower = status.toLowerCase();
  
  if (statusLower.includes('success') || statusLower === 'pass' || statusLower === 'valid') {
    return 'bg-qc-success-bg text-qc-success border border-qc-success/30';
  }
  
  if (statusLower.includes('warning') || statusLower === 'pending') {
    return 'bg-qc-warning-bg text-qc-warning border border-qc-warning/30';
  }
  
  if (statusLower.includes('error') || statusLower === 'fail' || statusLower === 'invalid') {
    return 'bg-qc-error-bg text-qc-error border border-qc-error/30';
  }
  
  return 'bg-bg-subtle text-text-secondary border border-border';
}

/**
 * Example usage with TypeScript-safe column definitions
 */
export const createQCTableColumns = (): Column[] => [
  {
    key: 'row_id',
    label: 'Row ID',
    width: '80px',
    align: 'center',
    type: 'number',
  },
  {
    key: 'column_name',
    label: 'Column',
    width: '150px',
    type: 'text',
  },
  {
    key: 'original_value',
    label: 'Original Value',
    type: 'code',
  },
  {
    key: 'corrected_value',
    label: 'AI Correction',
    type: 'code',
  },
  {
    key: 'confidence',
    label: 'Confidence',
    width: '100px',
    align: 'right',
    type: 'number',
    render: (value) => {
      const numValue = typeof value === 'number' ? value : 0;
      return `${(numValue * 100).toFixed(1)}%`;
    },
  },
  {
    key: 'status',
    label: 'Status',
    width: '120px',
    align: 'center',
    type: 'status',
  },
];
